import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Tabs } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import ExportButton from '../../../components/common/ExportButton';
import ObjectivesIcon from '../../../assets/icons/ic-objectives.svg?react';
import AlertIcon from '../../../assets/icons/ic-alert.svg?react';
import RiskAssessmentObjectivesTotal from './total/RiskAssessmentObjectivesTotal';
import RiskAssessmentFraudRisksTotal from './total/RiskAssessmentFraudRisksTotal';
import RiskAssessmentExportPanel from './total/RiskAssessmentExportPanel';

const { Title } = Typography;

// ==================== STYLED COMPONENTS ====================

const PageContainer = styled.div`
  padding: 0;
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const PageHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: #F3F4F6;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  color: #6B7280;

  &:hover {
    background: #E5E7EB;
    color: #1F2937;
  }

  &:active {
    background: #D1D5DB;
  }

  .anticon {
    font-size: 16px;
  }
`;

const PageTitle = styled(Title)`
  margin: 0 !important;
  font-family: Inter !important;
  font-weight: 500 !important;
  font-size: 18px !important;
  line-height: 100% !important;
  letter-spacing: 0.005em !important;
  color: #1F2937 !important;
  flex: 1;
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin-bottom: 24px;
  }

  .ant-tabs-tab {
    padding: 12px 16px;
    
    .ant-tabs-tab-btn {
      font-family: Inter;
      font-weight: 500;
      font-size: 14px;
      color: #6B7280;
    }

    svg path,
    svg circle {
      stroke: #6B7280;
      transition: stroke 0.3s;
    }

    &.ant-tabs-tab-active {
      .ant-tabs-tab-btn {
        color: #2563EB;
      }

      svg path,
      svg circle {
        stroke: #2563EB;
      }
    }

    &:hover {
      .ant-tabs-tab-btn {
        color: #2563EB;
      }

      svg path,
      svg circle {
        stroke: #2563EB;
      }
    }
  }

  .ant-tabs-ink-bar {
    background: #2563EB;
  }
`;

const TabLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 20px;
    height: 20px;
  }
`;

// ==================== COMPONENT ====================

const RiskAssessmentTotalPage: React.FC = () => {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();

  const getInitialTab = () => {
    if (tab === 'fraud-risks') return 'fraud-risks';
    return 'objectives';
  };

  const [activeTab, setActiveTab] = useState<string>(getInitialTab());
  const [exportPanelOpen, setExportPanelOpen] = useState(false);

  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab);
    navigate(`/risk-assessment-total/${newTab}`, { replace: true });
  };

  const tabItems = [
    {
      key: 'objectives',
      label: (
        <TabLabel>
          <ObjectivesIcon />
          Objectives
        </TabLabel>
      ),
      children: <RiskAssessmentObjectivesTotal />,
    },
    {
      key: 'fraud-risks',
      label: (
        <TabLabel>
          <AlertIcon />
          Fraud Risks
        </TabLabel>
      ),
      children: <RiskAssessmentFraudRisksTotal />,
    },
  ];

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderRow>
          <BackButton onClick={() => navigate('/risk-assessment')} title="Back to Risk Assessment">
            <LeftOutlined />
          </BackButton>
          <PageTitle level={2}>ICFR Risk Assessment - Consolidated View</PageTitle>
          <ExportButton onClick={() => setExportPanelOpen(true)} />
        </PageHeaderRow>
      </PageHeader>

      <StyledTabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
      />

      <RiskAssessmentExportPanel
        open={exportPanelOpen}
        onClose={() => setExportPanelOpen(false)}
      />
    </PageContainer>
  );
};

export default RiskAssessmentTotalPage;
